import React, { useState, useEffect } from 'react';
import ApprovalTable from './ApprovalTable';
import { usePointApprovalData } from './hooks';
import ManualPointDistribution from './ManualPointDistribution';
import { doc, updateDoc, deleteDoc, runTransaction, getDoc, collection, Timestamp } from 'firebase/firestore';
import { db, waitForAuthReady } from '../../../../utils/firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import Button from '../../../../components/ui/Button';
import Dialog from '../../../../components/ui/Dialog';
import SalaryPayslipBreakdown from '../../../../components/ui/SalaryPayslipBreakdown';
import {
  computeSalaryDeduction,
  parseSalaryTaxSettings,
  DEFAULT_SALARY_TAX_SETTINGS,
  buildSalaryPointHistoryFields,
  type SalaryTaxSettings,
} from '../../../../utils/salaryTax';

interface PointApprovalListProps {
  period: 'daily' | 'weekly' | 'monthly';
  deadlineMessage: string;
  /** 급여 세금 설정 저장 시마다 증가 — 아래 목록의 실수령 예상이 최신 설정을 반영하도록 함 */
  salaryTaxSettingsRevision?: number;
}

const PointApprovalList: React.FC<PointApprovalListProps> = ({
  period,
  deadlineMessage,
  salaryTaxSettingsRevision = 0,
}) => {
  const { currentUser } = useAuth();
  const teacher = currentUser as any;
  const { students, approvals, loading, error, getStudentName, getRoleName, getPointAmount, getPointAutoPay, getPointStatus } = usePointApprovalData();
  const [selectedApproval, setSelectedApproval] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [salaryTaxSettings, setSalaryTaxSettings] = useState<SalaryTaxSettings>(DEFAULT_SALARY_TAX_SETTINGS);

  useEffect(() => {
    if (!teacher?.schoolId || !teacher?.classId) return;
    let cancelled = false;
    (async () => {
      try {
        await waitForAuthReady();
        const snap = await getDoc(
          doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/salaryTaxSettings`, 'default')
        );
        if (!cancelled) {
          setSalaryTaxSettings(parseSalaryTaxSettings(snap.data() as Record<string, unknown> | undefined));
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacher?.schoolId, teacher?.classId, salaryTaxSettingsRevision]);

  const getEstimatedNet = (roleId: string) => {
    const gross = getPointAmount(roleId) || 0;
    return computeSalaryDeduction(gross, salaryTaxSettings).net;
  };

  const handleApprove = async (approvalId: string) => {
    try {
      const approvalRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`, approvalId);
      await updateDoc(approvalRef, {
        teacherApproved: true,
        teacherApprovedAt: new Date(),
        isApproved: true,
        updatedAt: new Date()
      });
    } catch (err) {
      console.error('포인트 승인 중 오류:', err);
      throw new Error('포인트 승인에 실패했습니다.');
    }
  };

  const handleReject = async (approvalId: string) => {
    try {
      const approvalRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`, approvalId);
      await updateDoc(approvalRef, {
        teacherApproved: false,
        teacherApprovedAt: new Date(),
        isApproved: false,
        updatedAt: new Date()
      });
    } catch (err) {
      console.error('포인트 거절 중 오류:', err);
      throw new Error('포인트 거절에 실패했습니다.');
    }
  };

  const handleDelete = async (approvalId: string) => {
    try {
      const approvalRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`, approvalId);
      await deleteDoc(approvalRef);
    } catch (err) {
      console.error('포인트 승인 삭제 중 오류:', err);
      throw new Error('포인트 승인 삭제에 실패했습니다.');
    }
  };

  const handleOpenInfoDialog = (approval: any) => {
    setSelectedApproval(approval);
  };

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds(prev =>
      checked ? [...prev, id] : prev.filter(_id => _id !== id)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filteredApprovals.map(a => a.id) : []);
  };

  const handleBulkApprove = async () => {
    setLoadingApprove(true);
    try {
      await waitForAuthReady();
      const taxSnap = await getDoc(
        doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/salaryTaxSettings`, 'default')
      );
      const taxSettings = parseSalaryTaxSettings(taxSnap.data() as Record<string, unknown> | undefined);

      for (const id of selectedIds) {
        const approval = filteredApprovals.find(a => a.id === id);
        if (!approval) continue;
        const studentRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`, approval.uid);
        await runTransaction(db, async (transaction) => {
          const studentSnap = await transaction.get(studentRef);
          if (!studentSnap.exists()) return;
          const currentPoints = studentSnap.data().points || 0;
          const gross = getPointAmount(approval.roleId) || 0;
          const deduction = computeSalaryDeduction(gross, taxSettings);
          transaction.update(studentRef, { points: currentPoints + deduction.net });
          const historyRef = doc(collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointHistories`));
          transaction.set(historyRef, {
            ...buildSalaryPointHistoryFields(
              approval.uid,
              deduction,
              `${getRoleName(approval.roleId)} 선택 지급`,
              period
            ),
            date: Timestamp.now(),
            createdAt: Timestamp.now()
          });
        });
      }
      setSelectedIds([]);
      window.location.reload();
    } catch (err) {
      alert('일괄 지급 중 오류가 발생했습니다.');
    } finally {
      setLoadingApprove(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        color: 'var(--Color-text-normal, #161616)',
        fontSize: '1rem',
        fontFamily: 'Pretendard, sans-serif'
      }}>
        로딩 중...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        padding: '1rem',
        background: 'var(--Color-status-negative, #FF3838)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontFamily: 'Pretendard, sans-serif'
      }}>
        {error}
      </div>
    );
  }

  const filteredApprovals = approvals
    .filter(approval => approval.salaryPeriod === period)
    .sort((a, b) => {
      const studentA = students.find(s => s.uid === a.uid);
      const studentB = students.find(s => s.uid === b.uid);
      return (studentA?.number || 0) - (studentB?.number || 0);
    });

  const pendingCount = filteredApprovals.filter(a => {
    const pointAutoPay = getPointAutoPay(a.roleId);
    if (pointAutoPay) return false;
    return !a.isApproved && a.teacherApproved !== false && a.pointManagerApproved !== false;
  }).length;

  const pxToRem = (value: number) => `${value / 16}rem`;

  const payslipDeduction =
    selectedApproval != null
      ? computeSalaryDeduction(
          getPointAmount(selectedApproval.roleId) || 0,
          salaryTaxSettings
        )
      : null;

  return (
    <div style={{
      alignSelf: 'stretch',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      gap: pxToRem(12),
      display: 'flex'
    }}>
      <Dialog
        open={selectedApproval != null}
        onClose={() => setSelectedApproval(null)}
        title="급여 명세서"
        actions={
          <Button variant="primary" size="medium" onClick={() => setSelectedApproval(null)}>
            닫기
          </Button>
        }
      >
        {selectedApproval && payslipDeduction && (
          <>
            <div
              style={{
                marginBottom: pxToRem(12),
                fontSize: pxToRem(14),
                fontFamily: 'Pretendard, sans-serif',
                color: 'var(--Color-text-strong)',
                fontWeight: 500,
              }}
            >
              {getStudentName(selectedApproval.uid)} · {getRoleName(selectedApproval.roleId)}
            </div>
            <div
              style={{
                fontSize: pxToRem(12),
                fontFamily: 'Pretendard, sans-serif',
                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                marginBottom: pxToRem(14),
                lineHeight: 1.5,
              }}
            >
              현재 저장된「급여 세금(공제) 설정」을 반영한 지급 예시입니다. 실제 지급 시에도 동일한 방식으로 적용됩니다.
            </div>
            <SalaryPayslipBreakdown
              variant="compact"
              grossAmount={payslipDeduction.gross}
              taxLines={payslipDeduction.taxLines}
              totalTax={payslipDeduction.totalTax}
              netAmount={payslipDeduction.net}
            />
          </>
        )}
      </Dialog>

      {/* 상태 표시 및 액션 버튼 */}
      <div style={{
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: pxToRem(12),
        display: 'inline-flex'
      }}>
        <div style={{
          flex: '1 1 0',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(8),
          display: 'flex'
        }}>
          <div style={{
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: pxToRem(4),
            display: 'flex'
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              현재
            </div>
            <div style={{
              color: 'var(--Color-fill-brand-color, #FF6F0F)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              {pendingCount}
            </div>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              명의 학생이 승인 대기 중 입니다
            </div>
          </div>
        </div>
        <div style={{
          width: pxToRem(248),
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: pxToRem(12),
          display: 'flex'
        }}>
          <Button
            width={pxToRem(118)}
            height={pxToRem(40)}
            variant="submit"
            onClick={handleBulkApprove}
            disabled={selectedIds.length === 0 || loadingApprove}
          >
            {loadingApprove ? '지급 중...' : '선택 포인트 지급'}
          </Button>
          <ManualPointDistribution 
            period={period}
            onSuccess={() => {
              window.location.reload();
            }}
            onError={(error) => {
              console.error('수동 지급 실패:', error);
            }}
          />
        </div>
      </div>

      {/* 알림 메시지 */}
      <div style={{
        alignSelf: 'stretch',
        height: pxToRem(40),
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: pxToRem(8),
        display: 'flex'
      }}>
        <div style={{
          alignSelf: 'stretch',
          flex: '1 1 0',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex'
        }}>
          <div style={{
            alignSelf: 'stretch',
            flex: '1 1 0',
            paddingTop: pxToRem(12),
            paddingBottom: pxToRem(12),
            paddingLeft: pxToRem(8),
            paddingRight: pxToRem(16),
            background: 'var(--Color-background-normal, white)',
            borderRadius: pxToRem(999),
            outline: '1.20px var(--Color-background-normal, white) solid',
            outlineOffset: '-1.20px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: pxToRem(8),
            display: 'inline-flex'
          }}>
            <div style={{
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex'
            }}>
              {/* Info 아이콘 */}
              <div style={{
                width: pxToRem(16),
                height: pxToRem(16),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="var(--Color-text-brand-color, #FF6F0F)" strokeWidth="1.5" fill="none"/>
                  <path d="M8 12V8M8 4H8.01" stroke="var(--Color-text-brand-color, #FF6F0F)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{
                textAlign: 'center',
                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                fontSize: pxToRem(13),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '500',
                lineHeight: pxToRem(19.5),
                wordWrap: 'break-word'
              }}>
                {deadlineMessage}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 테이블 */}
      <ApprovalTable
        approvals={filteredApprovals}
        students={students}
        getStudentName={getStudentName}
        getRoleName={getRoleName}
        getPointAmount={getPointAmount}
        getPointAutoPay={getPointAutoPay}
        getPointStatus={getPointStatus}
        handleOpenInfoDialog={handleOpenInfoDialog}
        handleApprove={handleApprove}
        handleReject={handleReject}
        handleDelete={handleDelete}
        selectedIds={selectedIds}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        getEstimatedNet={getEstimatedNet}
      />
    </div>
  );
};

export default PointApprovalList;
